import {
  FC,
  MouseEvent,
  createContext,
  forwardRef,
  useContext,
  useId,
  useState,
  type ComponentProps,
} from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import { Gavel, Policy, RecyclingOutlined } from '@mui/icons-material';
import {
  Badge,
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  IconButtonProps,
  ListItemAvatar,
  ListItemText,
  ListSubheader,
  Menu,
  MenuItem,
  MenuList,
  Skeleton,
  Typography,
  dividerClasses,
  listItemAvatarClasses,
  menuItemClasses,
} from '@mui/material';
import { listItemTextClasses } from '@mui/material/ListItemText';
import { enqueueSnackbar } from 'notistack';

import { LANGUAGE_FLAGS } from '@/views/Layout/constants';
import { signOut } from '../../auth/auth';
import {
  invalidateMyMessagesQuery,
  useMyMessagesQuery,
} from '../../hooks/queries/useMyMessagesQuery';
import { useAppVersion } from '../../hooks/useAppVersion';
import { Company, CompanyType, Person, Role, displayPerson, isAdminRole } from '../../models';
import { ItemType } from '../../models/ItemType';
import { myLocalStorage } from '../../myLocalStorage';
import {
  UpdateItemResponse,
  createItem,
  getImageForCompany,
  getImageForPerson,
  updateItem,
} from '../../network/useNetwork';
import {
  GA_EVENT_STUDENT_NAVIGATION,
  GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_OPENED,
  GA_EVENT_STUDENT_NAVIGATION_MENU_MESSAGES_OPENED,
  GA_EVENT_STUDENT_NAVIGATION_MENU_PROFILE_OPENED,
  trackEvent,
} from '../../utils/analytics';
import { ACCESSIBLE_SUBTEXT_ON_INTERACTIVE } from '../../utils/gradeBadgeColors';
import { MessagesIcon, SignOutIcon } from '../Icons';
import MyTaskView from '../Main/InternalDashboards/MyTaskView';
import { LanguageSelectorDialog } from './LanguageSelectorDialog';
import { Spacer } from './Spacer';

const userMenuMetadataRowSx = {
  [`& .${listItemTextClasses.secondary}`]: {
    color: ACCESSIBLE_SUBTEXT_ON_INTERACTIVE,
  },
};

const userMenuMetadataMenuItemSx = {
  ...userMenuMetadataRowSx,
  alignItems: 'flex-start',
  gap: 1,
  [`& .${listItemTextClasses.root}`]: {
    flex: '1 1 0%',
    minWidth: 0,
    maxWidth: '100%',
  },
} as const;

const ProfileMenuAppVersionContext = createContext<string>('');

const profileMenuVersionHeadingId = 'user-profile-menu-app-version-heading';

const ProfileMenuList = forwardRef<HTMLUListElement, ComponentProps<typeof MenuList>>(
  function ProfileMenuList({ children, sx: menuListSx, ...menuListProps }, ref) {
    const appVersion = useContext(ProfileMenuAppVersionContext);

    return (
      <Box
        sx={{
          display: 'flex',
          flexDirection: 'column',
          minWidth: 0,
          maxHeight: '100%',
          overflow: 'hidden',
        }}
      >
        <MenuList
          ref={ref}
          component="ul"
          {...menuListProps}
          subheader={
            <ListSubheader
              component="li"
              disableSticky
              role="presentation"
              sx={(theme) => ({
                px: 2,
                py: 1.25,
                display: 'flex',
                alignItems: 'center',
                color: theme.palette.text.secondary,
                lineHeight: theme.typography.button.lineHeight,
                minHeight: 48,
              })}
            >
              <Typography
                id={profileMenuVersionHeadingId}
                component="h2"
                variant="button"
                role="heading"
                aria-level={2}
                sx={{ m: 0, textTransform: 'uppercase' }}
              >
                {appVersion}
              </Typography>
            </ListSubheader>
          }
          sx={[
            {
              flex: 1,
              overflow: 'auto',
              minHeight: 0,
            },
            ...(Array.isArray(menuListSx) ? menuListSx : menuListSx ? [menuListSx] : []),
          ]}
        >
          {children}
        </MenuList>
      </Box>
    );
  }
);

const DEMO_COMPANY_IDS = {
  dev: [40], // Fullglass University Dev
  prod: [5835], // Fullglass University Prod
};

const canResetDemo = (company: Company | undefined, person: Person | undefined) => {
  const env = import.meta.env.VITE_PUBLIC_URL === 'https://fullglass.ai' ? 'prod' : 'dev';

  const companyIds = DEMO_COMPANY_IDS[env];

  return (
    company?.type === CompanyType.SCHOOL &&
    (isAdminRole() || person?.role === Role.ACADEMIC) &&
    company.id &&
    companyIds.includes(company.id)
  );
};

interface Props {
  selectedCourseSectionId?: number;
}

export const UserMenu: FC<Props & IconButtonProps> = (incomingProps) => {
  const { selectedCourseSectionId, ...iconButtonRest } = incomingProps;
  const {
    'aria-label': _omitAriaLabel,
    'aria-expanded': _omitAriaExpanded,
    'aria-haspopup': _omitAriaHaspopup,
    'aria-controls': _omitAriaControls,
    ...rest
  } = iconButtonRest;
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();

  const person: Person | undefined = myLocalStorage.getItem('person');
  const company: Company | undefined = myLocalStorage.getItem('company');

  const location = useLocation();
  const [anchorEl, setAnchorEl] = useState<Element | null>(null);
  const [isResetting, setIsResetting] = useState(false);
  const [isSigningOut, setIsSigningOut] = useState(false);
  const [isLanguageChangeModalOpen, setIsLanguageChangeModalOpen] = useState(false);
  const { data, isLoading: isMessagesLoading, hasNewMessages } = useMyMessagesQuery();

  const appVersion = useAppVersion();
  const profileMenuListId = useId();

  const handleAccountClick = (event: MouseEvent) => {
    setAnchorEl(event.currentTarget);
  };

  const handleCloseAccountMenu = () => {
    setAnchorEl(null);
  };

  if (!person) {
    return (
      <Button href="/signIn" variant="contained" sx={{ fontWeight: 'bold' }}>
        {t('signIn')}
      </Button>
    );
  }

  const personDisplayName = displayPerson(person);
  const profileMenuButtonLabel = t('userProfileMenuButton', { name: personDisplayName });

  const handleResetDemo = (e: MouseEvent) => {
    e.stopPropagation();

    setIsResetting(true);

    updateItem(
      ItemType.ResetDemo,
      {
        personId: person?.id,
        courseSectionIds: selectedCourseSectionId ? [selectedCourseSectionId] : [],
      },
      (response: UpdateItemResponse) => {
        setIsResetting(false);
        if (response.isSuccess) {
          enqueueSnackbar(t('success'), { variant: 'success' });
          invalidateMyMessagesQuery();
        } else {
          enqueueSnackbar(response.message, { variant: 'error' });
        }
      }
    );
  };

  const handleSignOut = async (event: MouseEvent) => {
    event.preventDefault();

    setIsSigningOut(true);

    createItem(ItemType.Signout, { personId: person.id }, async (response: UpdateItemResponse) => {
      if (!response.isSuccess) {
        enqueueSnackbar(response.message ?? 'Error', { variant: 'error' });
        setIsSigningOut(false);
      } else {
        try {
          await signOut();
          navigate('/signIn');
        } catch (error: any) {
          enqueueSnackbar(error?.message ?? t('error'), { variant: 'error' });
          navigate('/signIn');
        } finally {
          setIsSigningOut(false);
          handleCloseAccountMenu();
        }
      }
    });
  };

  const showResetDemo = canResetDemo(company, person);

  const profileMenuItemCount = 7 + (showResetDemo ? 1 : 0);
  let profileMenuItemIndex = 0;
  const nextProfileMenuItemA11y = () => ({
    'aria-posinset': ++profileMenuItemIndex,
    'aria-setsize': profileMenuItemCount,
  });

  return (
    <>
      {isAdminRole() && !location.pathname.includes('contents/document/') && (
        <>
          <Spacer />
          <MyTaskView />
        </>
      )}
      <IconButton
        {...rest}
        aria-haspopup="menu"
        aria-expanded={!!anchorEl}
        aria-controls={anchorEl ? profileMenuListId : undefined}
        aria-label={profileMenuButtonLabel}
        title={profileMenuButtonLabel}
        color="primary"
        onClick={handleAccountClick}
      >
        <Badge
          anchorOrigin={{
            vertical: 'top',
            horizontal: 'right',
          }}
          variant="dot"
          color="error"
          invisible={!hasNewMessages}
          slotProps={{
            root: { 'aria-hidden': true },
          }}
        >
          <Box component="span" aria-hidden sx={{ display: 'inline-flex', lineHeight: 0 }}>
            {getImageForPerson(person.imageUrl, personDisplayName, 40, true)}
          </Box>
        </Badge>
      </IconButton>
      <ProfileMenuAppVersionContext.Provider value={appVersion}>
        <Menu
          anchorEl={anchorEl}
          open={!!anchorEl}
          sx={{
            [`& .${menuItemClasses.root}`]: {
              height: 'auto',
              minHeight: 48,
              whiteSpace: 'normal',
              alignItems: 'flex-start',
            },
            [`& .${listItemAvatarClasses.root}`]: {
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              mr: 1,
            },
            [`&& .${dividerClasses.root}`]: {
              my: 0,
            },
          }}
          slots={{ list: ProfileMenuList }}
          slotProps={{
            paper: {
              sx: {
                marginTop: { xs: 0.5, sm: 0.75 },
                maxWidth: 'min(100vw - 16px, 360px)',
                overflow: 'hidden',
                display: 'flex',
                flexDirection: 'column',
              },
            },
            list: {
              id: profileMenuListId,
              dense: true,
              onClick: handleCloseAccountMenu,
            },
          }}
          onClose={handleCloseAccountMenu}
        >
          <MenuItem
            component="li"
            {...nextProfileMenuItemA11y()}
            sx={userMenuMetadataMenuItemSx}
            onClick={() => {
              trackEvent({
                action: GA_EVENT_STUDENT_NAVIGATION_MENU_PROFILE_OPENED,
                category: GA_EVENT_STUDENT_NAVIGATION,
              });
              navigate('/myProfile');
            }}
          >
            <ListItemAvatar>{getImageForPerson(person.imageUrl, '', 40)}</ListItemAvatar>
            <ListItemText
              sx={{ minWidth: 0 }}
              secondary={person.email}
              slotProps={{
                primary: {
                  sx: {
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'normal',
                  },
                },
                secondary: {
                  sx: {
                    wordBreak: 'break-word',
                    overflowWrap: 'anywhere',
                    whiteSpace: 'normal',
                    '&&': {
                      color: ACCESSIBLE_SUBTEXT_ON_INTERACTIVE,
                    },
                  },
                },
              }}
            >
              {personDisplayName}
            </ListItemText>
          </MenuItem>

          <MenuItem
            component="li"
            {...nextProfileMenuItemA11y()}
            onClick={() => {
              trackEvent({
                action: GA_EVENT_STUDENT_NAVIGATION_MENU_MESSAGES_OPENED,
                category: GA_EVENT_STUDENT_NAVIGATION,
              });
              navigate('/messages');
            }}
          >
            <ListItemAvatar>
              <Badge
                anchorOrigin={{
                  vertical: 'top',
                  horizontal: 'right',
                }}
                variant="dot"
                color="error"
                invisible={!hasNewMessages}
              >
                <MessagesIcon />
              </Badge>
            </ListItemAvatar>

            {isMessagesLoading ? (
              <Skeleton variant="rectangular" width={160} height={20} />
            ) : (
              <ListItemText>{t('xNewMessages', { x: data?.totalCount ?? 0 })}</ListItemText>
            )}
          </MenuItem>

          <MenuItem
            component="li"
            {...nextProfileMenuItemA11y()}
            disabled={!company}
            sx={{ ...userMenuMetadataMenuItemSx, mb: 1 }}
            onClick={() => {
              if (company?.id != null) {
                navigate(`/companyProfile/${company.id}`);
              }
            }}
          >
            <ListItemAvatar>{company && getImageForCompany(company, 40, '')}</ListItemAvatar>
            {company && (
              <ListItemText
                sx={{ minWidth: 0 }}
                secondary={company.domain}
                slotProps={{
                  primary: {
                    sx: {
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal',
                    },
                  },
                  secondary: {
                    sx: {
                      wordBreak: 'break-word',
                      overflowWrap: 'anywhere',
                      whiteSpace: 'normal',
                      '&&': {
                        color: ACCESSIBLE_SUBTEXT_ON_INTERACTIVE,
                      },
                    },
                  },
                }}
              >
                {company.name}
              </ListItemText>
            )}
          </MenuItem>

          <Divider component="li" />

          <MenuItem
            component="li"
            {...nextProfileMenuItemA11y()}
            sx={{ mt: 1, display: 'flex', flexDirection: 'row', alignItems: 'center' }}
            onClick={() => {
              setIsLanguageChangeModalOpen(true);
              trackEvent({
                action: GA_EVENT_STUDENT_NAVIGATION_MENU_LANGUAGE_OPENED,
                category: GA_EVENT_STUDENT_NAVIGATION,
              });
            }}
          >
            <ListItemAvatar>
              <Typography
                variant="h5"
                component="span"
                role="img"
                aria-label={t(`languages.${i18n.language}`)}
              >
                {LANGUAGE_FLAGS[i18n.language as keyof typeof LANGUAGE_FLAGS]}
              </Typography>
            </ListItemAvatar>
            <ListItemText sx={{ pt: 0.5 }}>{t('changeLanguage')}</ListItemText>
          </MenuItem>

          <Divider component="li" />

          <MenuItem
            component="li"
            sx={{ mt: 1 }}
            {...nextProfileMenuItemA11y()}
            onClick={() => navigate('/termsOfUse')}
          >
            <ListItemAvatar>
              <Gavel />
            </ListItemAvatar>
            <ListItemText>{t('termsOfUse')}</ListItemText>
          </MenuItem>

          <MenuItem
            component="li"
            {...nextProfileMenuItemA11y()}
            onClick={() => navigate('/privacyPolicy')}
          >
            <ListItemAvatar>
              <Policy />
            </ListItemAvatar>
            <ListItemText>{t('privacyPolicy')}</ListItemText>
          </MenuItem>

          <Divider component="li" />

          {showResetDemo && (
            <MenuItem
              component="li"
              {...nextProfileMenuItemA11y()}
              disabled={isResetting}
              onClick={handleResetDemo}
              sx={{ mt: 2 }}
            >
              <ListItemAvatar>
                {isResetting ? <CircularProgress size={24} /> : <RecyclingOutlined />}
              </ListItemAvatar>
              <ListItemText>{t('resetDemo')}</ListItemText>
            </MenuItem>
          )}

          {showResetDemo && <Divider component="li" />}

          <MenuItem
            component="li"
            sx={{ mt: 2 }}
            {...nextProfileMenuItemA11y()}
            aria-label={t('signOut')}
            disabled={isSigningOut}
            onClick={handleSignOut}
          >
            <ListItemAvatar>
              {isSigningOut ? <CircularProgress size={24} /> : <SignOutIcon />}
            </ListItemAvatar>
            <ListItemText>{t('signOut')}</ListItemText>
          </MenuItem>
        </Menu>
      </ProfileMenuAppVersionContext.Provider>

      <LanguageSelectorDialog
        key={i18n.language}
        open={isLanguageChangeModalOpen}
        onClose={() => setIsLanguageChangeModalOpen(false)}
      />
    </>
  );
};
